import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import { useActiveDeviceStream } from '../../device-manager/hooks';
import { onNoiseCancellationTypeChangingEvent } from '../../device-manager/types';
import { selectIsUserIODiagnosticsEnabled } from '../../flags/selectors';
import { deviceManager } from '../../goloom-sdk';
import { getAudioInputDeviceSelector, selectNoiseCancellationType } from '../../settings';

type InputMediaStreamContextProps = {
    mediaStream: MediaStream | null;
    mediaStreamIsReady: boolean;
};

export const InputMediaStreamContext = createContext<InputMediaStreamContextProps>({
    mediaStream: null,
    mediaStreamIsReady: false,
});
export const useInputMediaStreamContext = () => useContext(InputMediaStreamContext);
export const useInputDiagnosticMicStream = () => {
    const currentInputDevice = useSelector(getAudioInputDeviceSelector);
    const currentInputDeviceId = currentInputDevice?.deviceId;
    const currentInputDeviceLabel = currentInputDevice?.label;

    const isDiagnosticAvailable = useSelector(selectIsUserIODiagnosticsEnabled);
    const settingsNoiseCancellationType = useSelector(selectNoiseCancellationType);
    const [goloomNoiseCancellationType, setGoloomNoiseCancellationType] = useState(settingsNoiseCancellationType);
    const [mediaStream, setMediaStream] = useState<Maybe<MediaStream>>();
    const activeMediaStream = useActiveDeviceStream('audioinput', true);
    const audioRef = useRef<HTMLAudioElement>(new Audio());
    const [isAudioReady, setIsAudioReady] = useState(false);

    const currentStream = activeMediaStream ?? mediaStream ?? null;
    useEffect(() => {
        const handleChangeNoiseCancellationType = (
            noiseCancellationChangingEvent: onNoiseCancellationTypeChangingEvent
        ) => {
            if (deviceManager.audioInputActiveDevice) {
                if (noiseCancellationChangingEvent.applyToSessions.mainSession) {
                    setGoloomNoiseCancellationType(noiseCancellationChangingEvent.noiseCancellationType);
                }
            } else {
                if (noiseCancellationChangingEvent.applyToSessions.sessionPreview) {
                    setGoloomNoiseCancellationType(noiseCancellationChangingEvent.noiseCancellationType);
                }
            }
        };
        deviceManager.onNoiseCancellationTypeChanging.addListener(handleChangeNoiseCancellationType);
        return () => {
            deviceManager.onNoiseCancellationTypeChanging.removeListener(handleChangeNoiseCancellationType);
        };
    }, []);
    useLayoutEffect(() => {
        const audio = audioRef.current;
        if (currentStream) {
            audio.srcObject = currentStream;
            const onCanPlayThrough = () => {
                setIsAudioReady(true);
            };
            audio.addEventListener('canplaythrough', onCanPlayThrough);
            return () => {
                audio.removeEventListener('canplaythrough', onCanPlayThrough);
                setIsAudioReady(false);
                audio.srcObject = null;
            };
        }
    }, [currentStream]);

    useLayoutEffect(() => {
        if (!isDiagnosticAvailable) {
            return;
        }

        setIsAudioReady(false);

        if (deviceManager.audioInputActiveDevice) {
            return;
        }
        let isIgnore = false;
        const currentMediaStreamPromise = deviceManager.getLocalPreviewAudioStream(currentInputDeviceId);

        currentMediaStreamPromise.then((mediaStream) => {
            if (!isIgnore) {
                setMediaStream(mediaStream ?? undefined);
            }
        });
        return () => {
            isIgnore = true;
            deviceManager.reinitSessionPreview();
        };
    }, [currentInputDeviceId, isDiagnosticAvailable, goloomNoiseCancellationType, currentInputDeviceLabel]);

    return { mediaStream: activeMediaStream ?? mediaStream ?? null, mediaStreamIsReady: isAudioReady };
};
